import { get, set, del, clear, keys } from "idb-keyval";

const enc = new TextEncoder();
const dec = new TextDecoder();
const REC = "hb-data";
const DEVICE = "hb-device-key";

export const b64 = (buf) => {
  const u = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
  return btoa(s);
};
export const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function deviceKey() {
  let k = await get(DEVICE);
  if (!k) {
    k = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await set(DEVICE, k);
  }
  return k;
}

export async function deriveKey(pass, salt) {
  const base = await crypto.subtle.importKey("raw", enc.encode(pass), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: 250000, hash: "SHA-256" }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

async function encryptWith(key, obj) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(obj)));
  return { iv, data };
}
async function decryptWith(key, iv, data) {
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
  return JSON.parse(dec.decode(plain));
}

export const loadEnvelope = () => get(REC);

export async function openDevice(rec) {
  return decryptWith(await deviceKey(), rec.iv, rec.data);
}

// Raw 256-bit key bytes, identical to deriveKey(pass, salt) — used to wrap the key for Face ID / Touch ID
export async function deriveRaw(pass, salt) {
  const base = await crypto.subtle.importKey("raw", enc.encode(pass), "PBKDF2", false, ["deriveBits"]);
  return crypto.subtle.deriveBits({ name: "PBKDF2", salt, iterations: 250000, hash: "SHA-256" }, base, 256);
}

export const importRaw = (raw) => crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);

export async function openWithKey(rec, key) {
  return decryptWith(key, rec.iv, rec.data);
}

export async function openPasscode(rec, pass) {
  const key = await deriveKey(pass, rec.salt);
  const state = await decryptWith(key, rec.iv, rec.data);
  return { state, key };
}

// lock = { key, salt } when a passcode is set, otherwise null (device key)
export async function persist(state, lock) {
  if (lock) {
    const { iv, data } = await encryptWith(lock.key, state);
    await set(REC, { mode: "passcode", salt: lock.salt, iv, data, savedAt: Date.now() });
  } else {
    const { iv, data } = await encryptWith(await deviceKey(), state);
    await set(REC, { mode: "device", iv, data, savedAt: Date.now() });
  }
}

// Receipt files: stored separately, encrypted with the same key as the main data
const RKEY = (id) => `hb-receipt-${id}`;
const keyOf = async (lock) => (lock ? lock.key : deviceKey());

export async function saveReceipt(id, buf, lock) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await keyOf(lock), buf);
  await set(RKEY(id), { iv, data });
}

export async function loadReceipt(id, lock) {
  const rec = await get(RKEY(id));
  if (!rec) return null;
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, await keyOf(lock), rec.data);
}

export const deleteReceipt = (id) => del(RKEY(id));

export async function sweepReceipts(keepIds) {
  const keep = new Set(keepIds.map(RKEY));
  const all = (await keys()).filter((k) => typeof k === "string" && k.startsWith("hb-receipt-") && !keep.has(k));
  await Promise.all(all.map((k) => del(k)));
}

export async function reencryptReceipts(ids, oldLock, newLock) {
  for (const id of ids) {
    const buf = await loadReceipt(id, oldLock).catch(() => null);
    if (buf) await saveReceipt(id, buf, newLock);
  }
}

export async function makeLock(pass) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { key: await deriveKey(pass, salt), salt };
}

export async function wipeAll() {
  await del(REC);
  await clear();
  const keep = ["hb-analytics-off", "hb-move-banner-done"].map((k) => [k, localStorage.getItem(k)]);
  localStorage.clear();
  keep.forEach(([k, v]) => v && localStorage.setItem(k, v));
  if ("caches" in window) (await caches.keys()).forEach((k) => caches.delete(k));
}

// receipts: optional [{ id, data: base64 }] — travel inside the same (encrypted) payload
export async function exportBackup(state, password, receipts) {
  const payload = receipts?.length ? { state, receipts } : { state };
  if (!password) return JSON.stringify({ format: "household-budget-backup", version: 2, encrypted: false, ...payload });
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveKey(password, salt);
  const { iv, data } = await encryptWith(key, payload);
  return JSON.stringify({ format: "household-budget-backup", version: 2, encrypted: true, salt: b64(salt), iv: b64(iv), data: b64(data) });
}

// Returns { state, receipts }
export async function importBackup(text, password) {
  const obj = JSON.parse(text);
  if (obj.format !== "household-budget-backup") throw new Error("This file is not a Household Budget backup.");
  const unpack = (p) => ((obj.version || 1) >= 2 ? { state: p.state, receipts: p.receipts || [] } : { state: p, receipts: [] });
  if (!obj.encrypted) return unpack(obj.version >= 2 ? obj : obj.state);
  if (!password) throw new Error("This backup is encrypted. Enter its password.");
  const key = await deriveKey(password, unb64(obj.salt));
  let plain;
  try {
    plain = await decryptWith(key, unb64(obj.iv), unb64(obj.data));
  } catch {
    throw new Error("Incorrect backup password.");
  }
  return unpack(plain);
}

// Generic password-sealed JSON file (used for phone → computer receipt bundles)
export async function sealJSON(format, payload, password) {
  if (!password) return JSON.stringify({ format, version: 1, encrypted: false, payload });
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const { iv, data } = await encryptWith(await deriveKey(password, salt), payload);
  return JSON.stringify({ format, version: 1, encrypted: true, salt: b64(salt), iv: b64(iv), data: b64(data) });
}

export async function openJSON(format, text, password) {
  let obj;
  try { obj = JSON.parse(text); } catch { throw new Error("This file isn't a receipt bundle."); }
  if (obj.format !== format) throw new Error("This file isn't a receipt bundle.");
  if (!obj.encrypted) return obj.payload;
  if (!password) throw new Error("This bundle is password-protected. Enter its password.");
  try {
    return await decryptWith(await deriveKey(password, unb64(obj.salt)), unb64(obj.iv), unb64(obj.data));
  } catch {
    throw new Error("Incorrect bundle password.");
  }
}
